import Button from "atoms/buttons/button";
import ConversationMessageFeedbackRecord from "models/view-models/conversations/conversation-message-feedback-record";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import Icon from "atoms/icons/icon";
import React, { useState } from "react";
import TextArea from "atoms/forms/text-area";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ConversationMessageStatus } from "models/view-models/conversations/conversation-message-status";
import { ConversationStateStatus } from "utilities/atoms/conversations/current-conversation/conversation-atom.reducer";
import {
    FeedbackCategory,
    FeedbackCategoryDisplayNames,
} from "models/enumerations/messages/feedback-category";
import { FeedbackRating } from "models/enumerations/messages/feedback-rating";
import { Icons } from "atoms/constants/icons";
import { ToastManager } from "utilities/toast/toast-manager";
import { useConversation } from "utilities/atoms/conversations/current-conversation/use-conversation.hook";
import { useLocalization } from "utilities/hooks/use-localization";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface ChatMessageAdditionalFeedbackProps {
    feedbackCategory?: FeedbackCategory;
    feedbackRating: FeedbackRating;
    handleClose: () => void;
    message: ConversationMessageRecord;
    handleFeedbackCategory: React.Dispatch<
        React.SetStateAction<FeedbackCategory | undefined>
    >;
    handleShowAdditionalFeedback: React.Dispatch<React.SetStateAction<boolean>>;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-chat-message-additional-feedback";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatMessageAdditionalFeedback: React.FC<
    ChatMessageAdditionalFeedbackProps
> = ({
    feedbackCategory,
    feedbackRating,
    handleClose,
    message,
    handleFeedbackCategory,
    handleShowAdditionalFeedback,
}) => {
    const { t } = useLocalization();
    const {
        createMessageRating,
        updateMessageRating,
        currentConversation,
        status,
    } = useConversation();

    const [feedbackText, setFeedbackText] = useState<string>("");

    const MAX_COMMENT_LENGTH = 1000;
    const isAtLimit = feedbackText.length >= MAX_COMMENT_LENGTH;
    const isNearLimit = feedbackText.length > MAX_COMMENT_LENGTH * 0.8;

    const handleFeedbackSubmit = async (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e.preventDefault();

        if (
            currentConversation?.id == null ||
            message.status === ConversationMessageStatus.UpdatingFeedback
        ) {
            return;
        }
        try {
            if (message.feedback?.feedbackId) {
                const updatedFeedback = new ConversationMessageFeedbackRecord({
                    feedbackId: message.feedback.feedbackId,
                    rating: feedbackRating,
                    feedbackCategory: feedbackCategory,
                    feedbackComment: feedbackText,
                    conversationId: currentConversation.id,
                    messageId: message.id,
                });
                await updateMessageRating(updatedFeedback);
            } else {
                const newFeedback = new ConversationMessageFeedbackRecord({
                    rating: feedbackRating,
                    feedbackComment: feedbackText,
                    feedbackCategory: feedbackCategory,
                    conversationId: currentConversation.id,
                    messageId: message.id,
                });
                await createMessageRating(newFeedback);
            }
        } finally {
            handleShowAdditionalFeedback(false);
            handleFeedbackCategory(undefined);
            ToastManager.success(
                "Thank you for helping us improve our responses!"
            );
        }
    };

    const positiveCategories = [
        FeedbackCategory.Accurate,
        FeedbackCategory.UpToDate,
        FeedbackCategory.Helpful,
        FeedbackCategory.FollowedInstructions,
        FeedbackCategory.GoodSources,
        FeedbackCategory.Other,
    ];
    const negativeCategories = [
        FeedbackCategory.OutOfDate,
        FeedbackCategory.Inaccurate,
        FeedbackCategory.WrongSources,
        FeedbackCategory.TooLong,
        FeedbackCategory.TooShort,
        FeedbackCategory.Other,
    ];

    return (
        <div className={BASE_CLASS}>
            <div className={`${BASE_CLASS}__header`}>
                <div>
                    {feedbackRating === FeedbackRating.ThumbsUp
                        ? t("chat-message-feedback-positive")
                        : t("chat-message-feedback-negative")}
                </div>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        handleClose();
                    }}
                    size={ButtonSizes.XSmall}
                    style={ButtonStyles.Tertiary}>
                    <Icon type={Icons.Close}></Icon>
                </Button>
            </div>
            <div className={`${BASE_CLASS}__buttons`}>
                {feedbackRating === FeedbackRating.ThumbsUp
                    ? positiveCategories.map((category) => (
                          <Button
                              key={category}
                              style={ButtonStyles.Quaternary}
                              cssClassName={
                                  feedbackCategory === category
                                      ? `${BASE_CLASS}__category-button -selected`
                                      : `${BASE_CLASS}__category-button`
                              }
                              disabled={
                                  status ===
                                  ConversationStateStatus.MessageFeedbackLoading
                              }
                              onClick={() => handleFeedbackCategory(category)}>
                              {FeedbackCategoryDisplayNames[category]}
                          </Button>
                      ))
                    : negativeCategories.map((category) => (
                          <Button
                              key={category}
                              style={ButtonStyles.Quaternary}
                              cssClassName={
                                  feedbackCategory === category
                                      ? `${BASE_CLASS}__category-button -selected`
                                      : `${BASE_CLASS}__category-button`
                              }
                              disabled={
                                  status ===
                                  ConversationStateStatus.MessageFeedbackLoading
                              }
                              onClick={() => handleFeedbackCategory(category)}>
                              {FeedbackCategoryDisplayNames[category]}
                          </Button>
                      ))}
            </div>
            {feedbackCategory !== undefined && (
                <>
                    <div className={`${BASE_CLASS}__wrapper`}>
                        <div className={`${BASE_CLASS}__container`}>
                            <TextArea
                                id={"feedback-textarea"}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    const truncatedValue = newValue.slice(
                                        0,
                                        MAX_COMMENT_LENGTH
                                    );
                                    setFeedbackText(truncatedValue);
                                }}
                                onKeyDown={(e) => {
                                    // Prevent typing if already at limit (except for backspace, delete)
                                    if (
                                        feedbackText.length >=
                                            MAX_COMMENT_LENGTH &&
                                        ![
                                            "Backspace",
                                            "Delete",
                                            "ArrowLeft",
                                            "ArrowRight",
                                            "ArrowUp",
                                            "ArrowDown",
                                            "Home",
                                            "End",
                                        ].includes(e.key)
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                                placeholder={t("feedback-text-area")}
                                value={feedbackText}
                                rows={2}
                                spellcheck={true}
                                autofocus={true}
                                maxLength={MAX_COMMENT_LENGTH}
                            />
                        </div>
                    </div>

                    <div
                        className={`${BASE_CLASS}__character-count${isAtLimit ? " -at-limit" : isNearLimit ? " -near-limit" : ""}`}>
                        <div>{feedbackText.length}/</div>
                        <div>{MAX_COMMENT_LENGTH}</div>
                    </div>

                    <Button
                        accessibleText={t("feedback-text-area-submit")}
                        onClick={(e) => handleFeedbackSubmit(e)}
                        cssClassName={`${BASE_CLASS}__submit-button`}
                        size={ButtonSizes.Small}
                        disabled={
                            status ===
                            ConversationStateStatus.MessageFeedbackLoading
                        }
                        style={ButtonStyles.Tertiary}>
                        {t("submit")}
                    </Button>
                </>
            )}
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChatMessageAdditionalFeedback;
